import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "../../../../../../../../router/LocaleLink";
import { useFinancialAnalytics } from "../../../../../../../../api/services/financial-integration.hooks";
import { Paths } from "../../../../../../../../router/paths";
import {
  publicShariahKey,
  type PublicShariahSummary,
} from "../../../../../../../../seo/publicStockFields";
import { normalizeHalalStatus } from "../../../../../../../../utils/halalStatus";
import { HalalStatusBadge } from "../../../../../../../components/HalalStatusBadge/HalalStatusBadge";
import {
  BUSINESS_COMPLIANT_THRESHOLD,
  FINANCIAL_COMPLIANT_THRESHOLD,
} from "../../ShariahReport/shariahReportUtils";
import { descriptionExcerpt, failingScreens } from "./stockSummaryText";
import styles from "./ShariahSummaryCard.module.css";

const sr = "pages.topHalal.info.shariahReport.";

const STATUS_LABEL_KEY = {
  halal: `${sr}halal`,
  doubtful: `${sr}doubtful`,
  not_halal: `${sr}notHalal`,
  unqualified: `${sr}notQualified`,
  unknown: `${sr}faq.statusUnknown`,
} as const;

const formatPct = (value: number) => `${value.toFixed(2)}%`;

/**
 * The public Shariah summary: verdict, the three AAOIFI screens with their
 * limits, and the purification share. Server-rendered so search engines read
 * it; shown only when the server provides it (SHARIAH_PUBLIC_FIELDS=summary,
 * see src/server/config.server.ts). Renders nothing otherwise.
 */
export function ShariahSummaryCard({ ticker }: { ticker: string }) {
  const { t, i18n } = useTranslation();
  // Seeded by the route loader; never fetched from the browser.
  const { data: summary } = useQuery<PublicShariahSummary | null>({
    queryKey: publicShariahKey(ticker),
    queryFn: () => null,
    enabled: false,
    staleTime: Infinity,
  });
  const { data: analytics } = useFinancialAnalytics(
    { symbol: ticker },
    { enabled: false },
  );

  if (!summary) return null;

  const profile = analytics?.data?.companyProfile;
  const companyName = profile?.companyName?.trim() || ticker;
  const status = normalizeHalalStatus(summary.halalStatus);
  const businessPct = summary.nonCompliantRevenuePct + summary.doubtfulRevenuePct;

  const screens = [
    {
      key: "business" as const,
      label: t(`${sr}businessActivity`),
      value: businessPct,
      limit: BUSINESS_COMPLIANT_THRESHOLD,
      pass: businessPct <= BUSINESS_COMPLIANT_THRESHOLD,
    },
    {
      key: "debt" as const,
      label: t(`${sr}interestDebt`),
      value: summary.debtRatioPct,
      limit: FINANCIAL_COMPLIANT_THRESHOLD,
      pass: summary.debtRatioPct < FINANCIAL_COMPLIANT_THRESHOLD,
    },
    {
      key: "securities" as const,
      label: t(`${sr}interestSecurities`),
      value: summary.liquidityRatioPct,
      limit: FINANCIAL_COMPLIANT_THRESHOLD,
      pass: summary.liquidityRatioPct < FINANCIAL_COMPLIANT_THRESHOLD,
    },
  ];

  // Plain-language explanation of the numbers below, for readers and search
  // engines (all from data already on the page).
  const excerpt = descriptionExcerpt(profile?.description);
  const sector = profile?.sector?.trim();
  const industry = profile?.industry?.trim();
  const failing = failingScreens(screens);
  const screenValue = (key: (typeof screens)[number]["key"]) =>
    formatPct(screens.find((screen) => screen.key === key)!.value);

  // UTC so the server and the hydrating browser print the same date.
  const lastUpdated = summary.lastUpdated
    ? new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "medium",
        timeZone: "UTC",
      }).format(new Date(summary.lastUpdated))
    : null;

  return (
    <section className={styles.card} aria-labelledby="shariah-summary-heading">
      <div className={styles.header}>
        <h2 id="shariah-summary-heading" className={styles.heading}>
          {t("pages.stock.seo.introHeading")}
        </h2>
        <HalalStatusBadge status={summary.halalStatus} />
      </div>
      <p className={styles.verdict}>
        {status === "unknown"
          ? t("pages.stock.seo.introVerdictUnknown", { companyName, ticker })
          : t("pages.stock.seo.introVerdict", {
              companyName,
              ticker,
              statusLabel: t(STATUS_LABEL_KEY[status]),
            })}
      </p>
      <div className={styles.about}>
        <h3 className={styles.aboutHeading}>
          {t("pages.stock.summary.aboutHeading", { companyName })}
        </h3>
        {excerpt ? <p className={styles.aboutText}>{excerpt}</p> : null}
        {sector ? (
          <p className={styles.aboutText}>
            {industry
              ? t("pages.stock.summary.sectorIndustry", { companyName, ticker, sector, industry })
              : t("pages.stock.summary.sectorOnly", { companyName, ticker, sector })}
          </p>
        ) : null}
        {status === "halal" && failing.length === 0 ? (
          <p className={styles.aboutText}>
            {t("pages.stock.summary.passesAll", {
              business: screenValue("business"),
              debt: screenValue("debt"),
              securities: screenValue("securities"),
              businessLimit: String(BUSINESS_COMPLIANT_THRESHOLD),
              financialLimit: String(FINANCIAL_COMPLIANT_THRESHOLD),
            })}
          </p>
        ) : null}
        {status !== "halal" && status !== "unknown" && failing.length > 0 ? (
          <p className={styles.aboutText}>
            {t("pages.stock.summary.failsScreens", {
              screens: failing
                .map((key) => screens.find((screen) => screen.key === key)!.label)
                .join(", "),
            })}
          </p>
        ) : null}
        {/* Purification only makes sense for a stock one may hold. */}
        {(status === "halal" || status === "doubtful") && summary.purificationPct > 0 ? (
          <p className={styles.aboutText}>
            {t("pages.stock.summary.purification", {
              ticker,
              pct: formatPct(summary.purificationPct),
            })}
          </p>
        ) : null}
      </div>
      <dl className={styles.screens}>
        {screens.map((screen) => (
          <div key={screen.key} className={styles.screen}>
            <dt className={styles.screenLabel}>{screen.label}</dt>
            <dd className={styles.screenValue}>
              <span className={styles.value}>{formatPct(screen.value)}</span>
              <span className={styles.limit}>
                {t("pages.stock.shariahSummary.limit", { limit: String(screen.limit) })}
              </span>
              <span className={screen.pass ? styles.pass : styles.fail}>
                {t(screen.pass ? `${sr}pass` : `${sr}fail`)}
              </span>
            </dd>
          </div>
        ))}
        <div className={styles.screen}>
          <dt className={styles.screenLabel}>
            {t("pages.stock.shariahSummary.purification")}
          </dt>
          <dd className={styles.screenValue}>
            <span className={styles.value}>{formatPct(summary.purificationPct)}</span>
            <span className={styles.limit}>
              {t("pages.stock.shariahSummary.purificationHint")}
            </span>
            <Link to={Paths.PURIFICATION_CALCULATOR} className={styles.link}>
              {t("pages.stock.shariahSummary.purificationCalculator")}
            </Link>
          </dd>
        </div>
      </dl>
      <p className={styles.footnote}>
        {lastUpdated ? `${t(`${sr}lastUpdated`, { date: lastUpdated })} · ` : ""}
        {t("pages.stock.seo.introDisclaimer")}
      </p>
    </section>
  );
}
